/* eslint-disable react-hooks/exhaustive-deps */
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";
import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import * as Yup from "yup";
import { postApiCaller } from "../../libs/apiCaller";
import { toast } from "react-toastify";
import { useUnauthorizedNavigation } from "../../libs/UnauthorisedPage";

const EditBillingAddress = ({
  isOpen,
  onClose,
  profileInfo,
  getConsumerDetails,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const redirectToLogin = useUnauthorizedNavigation();

  useEffect(() => {
    if (
      (profileInfo?.billing_apt_no ||
        profileInfo?.billing_street ||
        profileInfo?.billing_state ||
        profileInfo?.billing_city ||
        profileInfo?.billing_postal_code) &&
      isOpen
    ) {
      formik.setValues({
        billing_apt_no: profileInfo?.billing_apt_no || "",
        address: profileInfo?.billing_street || "",
        state: profileInfo?.billing_state || "",
        city: profileInfo?.billing_city || "",
        postalCode: profileInfo?.billing_postal_code || "",
      });
    }
  }, [profileInfo, isOpen]);

  const formik = useFormik({
    initialValues: {
      billing_apt_no: "",
      address: "",
      state: "",
      city: "",
      postalCode: "",
    },
    validationSchema: Yup.object({
      billing_apt_no: Yup.string(),
      address: Yup.string().required("Address is required"),
      state: Yup.string().required("Province/State is required"),
      city: Yup.string().required("City is required"),
      postalCode: Yup.string().required("Postal Code is required"),
    }),
    onSubmit: async (values) => {
      setIsLoading(true);
      const endPoint = `consumer_update`;
      const payload = {
        type: "billing",
        consumer_id: profileInfo?.id,
        billing_apt_no: values.billing_apt_no,
        billing_street: values.address,
        billing_city: values.city,
        billing_state: values.state,
        billing_postal_code: values.postalCode,
      };
      try {
        const response = await postApiCaller(endPoint, payload);
        if (response?.data?.response_code === 200) {
          setIsLoading(false);
          onClose();
          formik.resetForm();
          toast.success(
            response?.data?.response_message ||
              "Billing Address Updated Successfully"
          );
          getConsumerDetails && getConsumerDetails();
        } else if (response?.status === 500) {
          toast.error(response?.response?.data?.message);
          setIsLoading(false);
        } else if (response.data.response_code === 403) {
          setIsLoading(false);
          redirectToLogin();
        } else {
          toast.error(response?.data?.response_message);
          setIsLoading(false);
        }
      } catch (error) {
        toast.error(error?.message);
        setIsLoading(false);
      }
    },
  });

  return (
    <Dialog open={isOpen} onClose={() => {}} className="relative z-50">
      <DialogBackdrop className="fixed inset-0 bg-gray-500/75 transition-opacity" />

      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4">
          <DialogPanel className="relative w-full transform overflow-hidden bg-white text-left shadow-xl transition-all rounded-lg sm:max-w-md md:max-w-lg">
            <div className="bg-[#21140E0F] px-4 pb-6 pt-3 sm:px-6">
              <div className="flex justify-between items-center mb-4">
                <DialogTitle className="text-lg sm:text-xl font-bold text-[#284184]">
                  Edit Billing Address
                </DialogTitle>
                <button
                  onClick={() => {
                    onClose();
                    formik.resetForm();
                  }}
                  className="text-[#1C2024] hover:bg-gray-100 p-1.5 rounded-full transition-colors"
                >
                  <span className="text-2xl sm:text-4xl leading-none">
                    &times;
                  </span>
                </button>
              </div>

              <form onSubmit={formik.handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm sm:text-base mb-1.5">
                    Apt No
                  </label>
                  <input
                    type="text"
                    name="billing_apt_no"
                    className="w-full rounded-md border border-gray-300 py-2 px-3 text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-shadow"
                    placeholder="Enter Apt No"
                    value={formik.values.billing_apt_no}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                  {formik.touched.billing_apt_no &&
                    formik.errors.billing_apt_no && (
                      <p className="text-red-500 text-xs mt-1">
                        {/* {formik.errors.billing_apt_no} */}
                      </p>
                    )}
                </div>
                <div>
                  <label className="block text-sm sm:text-base mb-1.5">
                    Address *
                  </label>
                  <input
                    type="text"
                    name="address"
                    className="w-full rounded-md border border-gray-300 py-2 px-3 text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-shadow"
                    placeholder="Enter Address"
                    value={formik.values.address}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                  {formik.touched.address && formik.errors.address && (
                    <p className="text-red-500 text-xs mt-1">
                      {formik.errors.address}
                    </p>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="relative">
                    <label className="block text-sm sm:text-base mb-1.5">
                      Province/State *
                    </label>
                    <input
                      type="text"
                      name="state"
                      className="w-full rounded-md border border-gray-300 py-2 px-3 text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-shadow"
                      placeholder="Enter Province/State"
                      value={formik.values.state}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />
                    {formik.touched.state && formik.errors.state && (
                      <p className="text-red-500 text-xs mt-1">
                        {formik.errors.state}
                      </p>
                    )}
                  </div>
                  <div className="relative">
                    <label className="block text-sm sm:text-base mb-1.5">
                      City *
                    </label>
                    <input
                      type="text"
                      name="city"
                      className="w-full rounded-md border border-gray-300 py-2 px-3 text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-shadow"
                      placeholder="Enter City"
                      value={formik.values.city}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />
                    {formik.touched.city && formik.errors.city && (
                      <p className="text-red-500 text-xs mt-1">
                        {formik.errors.city}
                      </p>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm sm:text-base mb-1.5">
                    Postal Code *
                  </label>
                  <input
                    type="text"
                    name="postalCode"
                    className="w-full rounded-md border border-gray-300 py-2 px-3 text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-shadow"
                    placeholder="Enter Postal Code"
                    value={formik.values.postalCode}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                  {formik.touched.postalCode && formik.errors.postalCode && (
                    <p className="text-red-500 text-xs mt-1">
                      {formik.errors.postalCode}
                    </p>
                  )}
                </div>

                <div className="flex flex-col sm:flex-row gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => {
                      onClose();
                      formik.resetForm();
                    }}
                    className="w-full rounded-md border font-semibold border-[#F04438] bg-white px-4 py-2 text-sm text-[#F04438] hover:bg-red-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="w-full rounded-md border font-semibold border-transparent bg-[#284184] px-4 py-2 text-sm text-white hover:bg-[#1f3266] transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                    disabled={isLoading}
                  >
                    {isLoading ? "Loading..." : "Save"}
                  </button>
                </div>
              </form>
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
};

export default EditBillingAddress;
